import { useState, useCallback, useMemo, useEffect } from "react"

const POST_DELIMITER = "============="

export const useThread = (initialContent = "") => {
  const initialPosts = useMemo(() => {
    const splitPosts = initialContent?.split(POST_DELIMITER)
        .map(p => p ? p.trim() : "")
        .filter(p => p !== "") || []

    return splitPosts.length > 0 ? splitPosts : [""]
  }, [initialContent])

  const [posts, setPosts] = useState(initialPosts)

  const updatePost = useCallback((index, content) => {
    setPosts(prev => {
      const newPosts = [...prev]
      newPosts[index] = content
      return newPosts
    })
  }, [])

  const addPost = useCallback(() => {
    setPosts(prev => [...prev, ""])
  }, [])

  const removePost = useCallback((index) => {
    setPosts(prev => {
      if (prev.length <= 1) return prev
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const joinedContent = useMemo(() => posts.join(`\n\n${POST_DELIMITER}\n\n`), [posts])

  return {
    posts,
    updatePost,
    addPost,
    removePost,
    joinedContent,
  }
}

export const useAutoSaveThread = (
  joinedContent,
  initialContent,
  saveUrl,
  saveMethod = 'patch',
  { onSuccess, onError } = {}
) => {
  const [isSaving, setIsSaving] = useState(false)
  const [saveTimeout, setSaveTimeout] = useState(null)

  useEffect(() => {
    if (saveTimeout) clearTimeout(saveTimeout)

    if (joinedContent === initialContent) {
      if (isSaving) setIsSaving(false)
      return
    }

    const timeout = setTimeout(() => {
      setIsSaving(true)

      const data = { post: { content: joinedContent } }
      const options = {
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => {
          setIsSaving(false)
          if (onSuccess) onSuccess(page)
        },
        onError: (errors) => {
          setIsSaving(false)
          if (onError) onError(errors)
        },
      }

      if (saveMethod.toLowerCase() === 'post') {
        router.post(saveUrl, data, options)
      } else {
        router.patch(saveUrl, data, options)
      }
    }, 750)

    setSaveTimeout(timeout)

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [joinedContent, initialContent, saveUrl, saveMethod, onSuccess, onError])

  return { isSaving }
}
